import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {Debouncer, logger, UIUtil, Util} from 'yougile-util'
import {
  CheckInviteReq,
  GenResp,
  KeyReq,
  KeyResp,
  RegisterReq,
  RegisterResp
} from 'yougile-model'
import {ClientUtilities} from '@client/client-utilities'
import {Backend} from '@common/backend'
import {FileChooser} from '@common/file-chooser'
import {Tracking, TrackingEvent} from '@common/tracking'
import {FileUploadProgress} from '@components/file-upload-progress'
import {LoginHelper} from '@components/login/login-helper'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'

interface State {
  initialEmail: string
  stage: 'form' | 'error' | 'wait' | 'wait-ready'
  muted: boolean
  progress: number
  progressLabel: string

  // if the registration requires phone (first user in company)
  withPhone: boolean
  phoneCorrect: boolean
  submitPressed?: boolean
  isPending: boolean
  isSoftPending: boolean

  regForm: {
    name: string
    company: string
    email: string
    password: string
    photo: string
    phone: string
    wait: boolean
    error: string
    typing: string
  }
}

export class RegisterStore {
  state: State

  constructor() {
    ClientUtilities.setLangFromUrlOrBrowser()

    // TODO restore logic with show progress, was commented in old angular file
    const progressLabels = [
      'Идёт подготовка системы',
      'Создание аккаунта',
      'Создание компании и проектов',
      'Настройка системы'
    ]

    PageService.getPage
    const email = Util.cleanEmail(PageService.getSearchParam('email'))
    const key = PageService.getSearchParam('key')
    const withPhone = !!PageService.getSearchParam('init')

    this.state = {
      progress: 0,
      progressLabel: progressLabels[0],
      stage: 'form',
      withPhone,
      phoneCorrect: true,
      initialEmail: email,
      muted: true,
      isPending: false,
      isSoftPending: false,
      regForm: {
        name: '',
        email,
        company: '',
        password: '',
        photo: '',
        phone: '',
        wait: false,
        error: '',
        typing: ''
      }
    }

    makeAutoObservable(
      this,
      {
        // TODO: Why we need this to be observable.shallow?
        state: observable.shallow
      },
      {autoBind: true}
    )

    if (Lang.getLang() === 'ru') {
      Backend.saveLocal('lang', 'ru')
    }
    void this.checkKey(email, key)
  }

  private async checkKey(email: string, key: string): Promise<void> {
    if (!Util.isEmail(email)) {
      this.clearError()
      return
    }
    if (!globals.standalone) {
      return
    }
    const req: CheckInviteReq = {email, key}
    if (
      !email ||
      !key ||
      (await Backend.postJSON<GenResp>('/check/invite', req)).result !== 'ok'
    ) {
      this.state.stage = 'error'
    }

    Backend.postJSON('/check/invite', req).catch(() => {
      this.state.stage = 'error'
    })
  }

  private allFieldsCorrect(): boolean {
    this.state.regForm = {...this.state.regForm, error: this.getError()}
    return !this.state.regForm.error
  }

  showInvalidName(): boolean {
    if (!this.state.submitPressed) {
      return false
    }
    return !this.state.regForm.name || this.state.regForm.name.trim().length < 3
  }
  showInvalidCompany(): boolean {
    if (!this.state.submitPressed) {
      return false
    }
    return this.state.withPhone && this.state.regForm.company.trim().length < 3
  }
  showInvalidEmail(): boolean {
    if (!this.state.submitPressed) {
      return false
    }
    return !Util.isEmail(this.state.regForm.email)
  }
  showInvalidPass(): boolean {
    if (!this.state.submitPressed) {
      return false
    }
    return (
      !this.state.regForm.password || this.state.regForm.password.length < 6
    )
  }
  showInvalidPhone(): boolean {
    if (!this.state.submitPressed) {
      return false
    }
    return (
      this.state.withPhone &&
      (!this.state.regForm.phone || !Util.isPhone(this.state.regForm.phone))
    )
  }

  private getError(): string {
    switch (true) {
      case !this.state.regForm.name:
        return Lang.msg.nameEmpty
      case this.state.regForm.name.trim().length < 3:
        return Lang.msg.nameShort

      case this.state.withPhone && this.state.regForm.company.trim().length < 3:
        return Lang.msg.reg.companyErr

      case !Util.isEmail(this.state.regForm.email):
        return Lang.msg.emailIncorrect2

      case this.state.withPhone && !this.state.regForm.phone:
        return Lang.msg.phoneEmpty
      case this.state.withPhone && !Util.isPhone(this.state.regForm.phone):
        return Lang.msg.phoneIncorrect

      case !this.state.regForm.password:
        return Lang.msg.noPassword
      case this.state.regForm.password.length < 6:
        return Lang.msg.passwordShort

      default:
        return ''
    }
  }

  isNameCorrect(): boolean {
    return this.state.regForm.name.trim().length >= 3
  }

  isCompanyNameCorrect(): boolean {
    return this.state.regForm.company.trim().length >= 3
  }

  isPhoneCorrect(): boolean {
    return Util.isPhone(this.state.regForm.phone)
  }

  isPasswordCorrect(): boolean {
    return this.state.regForm.password.length >= 6
  }

  unmute(): void {
    this.state.muted = false
  }

  async upload(e: MouseEvent): Promise<void> {
    if (this.state.regForm.wait) {
      return
    }
    const file = await FileChooser.chooseFile(e, false)
    if (!UIUtil.isSupportedImage(file)) {
      MessageBox.warn(Lang.msg.imageTypeUnsupported)
      return
    }
    const url = await FileUploadProgress.saveFile(file)
    this.state.regForm = {...this.state.regForm, photo: url}
  }

  getPhotoStyle(): {backgroundImage?: string} {
    if (!this.state.regForm.photo) {
      return {}
    }
    const avatar = this.state.regForm.photo
    const avaPrev = avatar.split('_____previews_')[0]
    const backgroundImage =
      avaPrev !== avatar
        ? `url("${avaPrev}-256-preview.png")`
        : `url("${avatar}")`
    return {backgroundImage}
  }

  async submit(): Promise<void> {
    this.state.submitPressed = true

    if (this.state.regForm.wait || !this.allFieldsCorrect()) {
      return
    }
    const email = Util.cleanEmail(PageService.getSearchParam('email'))
    const key = PageService.getSearchParam('key')

    this.state.regForm.wait = true
    const {name, photo, password, phone, company} = this.state.regForm
    const rReq: RegisterReq = {
      email,
      name,
      photo,
      password,
      phone,
      key,
      company,
      lang: Lang.getLang()
    }
    try {
      this.state.isPending = true
      pendingDebouncer.runLatest(() => {
        this.state.isSoftPending = true
      })

      const resp = await Backend.postJSON<RegisterResp>('/data/register', rReq)
      const keyReq: KeyReq = {userId: resp.userId, password: rReq.password}
      const keyRes = await Backend.postJSON<KeyResp>('/data/key', keyReq)

      const trackingEvent = this.state.withPhone
        ? TrackingEvent.regPageCompleteRegister
        : TrackingEvent.regPageCompleteInvite

      Tracking.send(trackingEvent)

      if (keyRes.nextStep && keyRes.userId && keyRes.key) {
        LoginHelper.set2FaDataToStorage({
          twoFactorStep: keyRes.nextStep,
          loginFormEmail: this.state.regForm.email,
          loginFormPassword: this.state.regForm.password,
          userId: keyRes.userId,
          authKey: keyRes.key
        })

        setTimeout(() => {
          this.enter()
        }, 300)

        return
      }

      if (keyRes.userId && keyRes.key) {
        Backend.saveCredentials(keyRes.userId, keyRes.key)
      }

      setTimeout(() => {
        this.enter()
      }, 300)
    } catch (error: any) {
      if (error instanceof Error) {
        logger.error(
          `Server error while performing registration`,
          error.message
        )
        this.setError(`${Lang.msg.serverError}: ${error.message}`)
      } else {
        logger.error('Unknown error while performing registration', error)
        this.setError(Lang.msg.serverError)
      }
      this.state.regForm.wait = false
    } finally {
      this.state.isPending = false
      pendingDebouncer.runLatest(() => {
        this.state.isSoftPending = false
      })
    }
  }

  setError(text: string): void {
    this.state.regForm = {
      ...this.state.regForm,
      error: text
    }
  }

  clearError(): void {
    this.state.regForm = {
      ...this.state.regForm,
      error: ''
    }
  }

  enter(): void {
    location.href = '/team/'
  }

  keyUp(fieldName: string, e: React.KeyboardEvent): void {
    this.state.regForm.typing = fieldName
    typingDebouncer.runLatest(() => {
      this.state.regForm.typing = ''
    })

    this.clearError()
    if (e.key === 'Enter') {
      void this.submit()
    }
  }

  setName(text: string): void {
    this.state.regForm.name = text.trim()
  }

  setCompany(text: string): void {
    this.state.regForm.company = text.trim()
  }

  setPhone(text: string): void {
    this.state.regForm.phone = text.trim()
    this.state.phoneCorrect = Util.isPhone(this.state.regForm.phone)
  }

  setEmail(text: string): void {
    this.state.regForm.email = text.trim()
  }

  setPassword(text: string): void {
    this.state.regForm.password = text
  }
}

export const [RegisterContext, useRegisterStore] = createUseStore(RegisterStore)

const typingDebouncer = new Debouncer(1000)
const pendingDebouncer = new Debouncer(350)
